// audio.ts - Radically simplified audio using native HTML5 Audio
// ~100 lines max, single track, minimal state management
//
// Phase 2 (issue #84): converted off the classic global model. `AudioModule` is
// now `export`ed and loaded through the bundle entry (src/main.js) rather than
// the classic script-loader. This module uses no three.js, so there is no
// `import * as THREE`. Every consumer imports `AudioModule` directly now, so the
// previous `window.AudioModule` namespace bridge has been removed.
//
// Phase 3 (issue #84): renamed `.js` -> `.ts`, dropping the now-implied
// `// @ts-check` pragma and promoting the `AudioStatus` JSDoc typedef into a real
// `interface` plus typed fields/params. Every edit is type-only/erasable, so
// esbuild (Vite) and Node's native type-stripping run it exactly as before —
// behaviour is unchanged.
//
// Design principles:
// 1. Native HTML5 Audio element - no library dependencies
// 2. Single track only (bad_cat) - no track switching complexity
// 3. Only 2 state variables - muted and initialized
// 4. No pre-loading - load when user clicks play
// 5. No visibility change handling - let browser manage it
// 6. No retry prompts - if it doesn't work, it doesn't work
//
// To disable: Set AUDIO_ENABLED = false

import { Sfx } from './sfx.js';
import { safeGetItem, safeSetItem } from './offline/offline-store.js';

const AUDIO_ENABLED = true;

/**
 * Status object returned by getStatus(). The native HTML5 implementation only
 * populates the first fields; bufferLoaded/contextReady/contextState are legacy
 * (Howler/AudioContext-era) fields that callers (snowglider.ts checkAudioStatus)
 * still probe — kept optional so those compat checks type-check and read as
 * undefined at runtime.
 */
interface AudioStatus {
  initialized: boolean;
  disabled: boolean;
  muted: boolean;
  playing: boolean;
  currentTrack?: string;
  bufferLoaded?: boolean;
  contextReady?: boolean;
  contextState?: string;
}

export const AudioModule = (function() {
  let audio: HTMLAudioElement | null = null;
  let muted = false;
  let initialized = false;
  // Live showMessage() toasts (node + auto-remove timer) so teardown() can clear any that
  // are still on screen — they're fixed-position z-index:3000 divs that would otherwise
  // linger over the host page after an unmount until their timeout fires.
  const activeMessages = new Set<{ el: HTMLElement; timer: ReturnType<typeof setTimeout> }>();

  // Run messages belong to the active run, not the result dialog. Clearing them
  // must leave music preferences and the sound control intact for replay.
  function clearMessages(): void {
    for (const { el, timer } of activeMessages) { clearTimeout(timer); el.remove(); }
    activeMessages.clear();
  }

  const AUDIO_PATH = 'assets/skullbeatz_bad_cat.mp3';

  // Load mute preference from localStorage
  function loadPreferences() {
    const stored = safeGetItem('snowgliderMuted');
    if (stored !== null) {
      muted = stored === 'true';
    }
  }

  // Save mute preference
  function savePreferences() {
    safeSetItem('snowgliderMuted', String(muted));
  }

  // Create the audio element
  function createAudio(): HTMLAudioElement {
    if (audio) return audio;

    audio = new Audio(AUDIO_PATH);
    audio.loop = true;
    audio.volume = 0.5;

    audio.addEventListener('error', (e) => {
      const mediaEl = e.target as HTMLMediaElement;
      console.warn('[Audio] Load error:', mediaEl?.error?.message || 'unknown');
    });

    return audio;
  }

  // Create minimal UI - just a mute button
  function createUI() {
    if (!AUDIO_ENABLED) return;
    if (document.getElementById('audioControlBtn')) return;

    const btn = document.createElement('button');
    btn.id = 'audioControlBtn';
    btn.style.cssText = `
      position: fixed; bottom: var(--hud-bottom, 20px); right: var(--hud-right, 20px); z-index: 1000;
      width: 44px; height: 44px; border-radius: 50%;
      border: none; background: rgba(0,0,0,0.6); color: white;
      font-size: 20px; cursor: pointer;
    `;
    updateButtonUI(btn);

    // Button UI is already updated by toggleMute().
    const handleToggle = () => { AudioModule.toggleMute(); };

    // Desktop / pointer devices.
    btn.addEventListener('click', handleToggle);

    // Mobile: the game registers a document-level `touchstart` handler that calls
    // preventDefault() (controls.ts), which suppresses the browser's synthesized
    // `click` for the tap — so the click listener above never fires on touch and
    // the button appears dead. Handle the tap explicitly on `touchstart`, mirroring
    // the reset/camera buttons. stopPropagation() keeps the tap fully out of the
    // control system: because the document handler never *sees* this touchstart it
    // never records the touch id in touchState.touches, so there is no orphaned
    // entry for the (unhandled) touchend to leave behind — which would otherwise
    // block the `touches.length === 0` reset and leave controls stuck. The unhandled
    // touchend still bubbles to the document, where its `delete` is a harmless no-op.
    // preventDefault() suppresses the synthesized click so mute isn't toggled twice.
    btn.addEventListener('touchstart', (e) => {
      e.preventDefault();
      e.stopPropagation();
      handleToggle();
    }, { passive: false });

    document.body.appendChild(btn);
  }

  function updateButtonUI(btn: HTMLElement) {
    btn.textContent = muted ? '🔇' : '🔊';
    btn.title = muted ? 'Unmute' : 'Mute';
    btn.setAttribute('aria-label', 'Sound');
    btn.setAttribute('aria-pressed', String(!muted));
  }

  // Public API
  return {
    // _scene accepted (and ignored) for backward-compat with the old Three.js
    // Audio API surface; the native HTML5 implementation needs no scene.
    init: function(_scene?: unknown) {
      if (!AUDIO_ENABLED) return { initialized: false, disabled: true };
      if (initialized) return { initialized: true };

      loadPreferences();
      initialized = true;
      console.log('[Audio] Initialized (simplified)');
      return { initialized: true };
    },

    setupUI: function() {
      if (!AUDIO_ENABLED) return;
      createUI();
    },

    startAudio: function() {
      if (!AUDIO_ENABLED || muted) return false;

      const el = createAudio();
      el.play().then(() => {
        console.log('[Audio] Playing');
      }).catch((e: unknown) => {
        console.warn('[Audio] Play failed:', e instanceof Error ? e.message : e);
      });
      return true;
    },

    toggleMute: function() {
      if (!AUDIO_ENABLED) return false;

      muted = !muted;
      savePreferences();

      // The single mute button governs both subsystems: the background music here
      // and the procedural sound effects (#158), which share the 'snowgliderMuted'
      // preference key.
      Sfx.setMuted(muted);

      if (audio) {
        if (muted) {
          audio.pause();
        } else {
          audio.play().catch(() => {});
        }
      }

      // Update button if exists
      const btn = document.getElementById('audioControlBtn');
      if (btn) updateButtonUI(btn);

      return muted;
    },

    setVolume: function(level: number) {
      if (!AUDIO_ENABLED || !audio) return;
      audio.volume = Math.max(0, Math.min(1, level));
    },

    enableSound: function(enable: boolean) {
      if (!AUDIO_ENABLED) return;
      if (enable && !muted && audio) {
        audio.play().catch(() => {});
      } else if (!enable && audio) {
        audio.pause();
      }
    },

    getStatus: function(): AudioStatus {
      if (!AUDIO_ENABLED) {
        return { initialized: false, disabled: true, muted: true, playing: false };
      }
      return {
        initialized,
        disabled: false,
        muted,
        playing: audio ? !audio.paused : false,
        currentTrack: 'bad_cat'
      };
    },

    isEnabled: function() {
      return AUDIO_ENABLED;
    },

    // Compatibility stubs for existing code. preloadAudio accepts (and ignores) a
    // track name for parity with the old Howler API surface — the boot
    // script-loader still calls preloadAudio('bad_cat').
    preloadAudio: function(_track?: string) { return Promise.resolve(); },
    playPreloadedAudio: function() { return this.startAudio(); },
    resumeAudioContext: function() { return Promise.resolve(); },
    changeTrack: function() { return false; },
    addAudioListener: function(_listener?: unknown) {},
    showMessage: function(msg: string, duration = 3000) {
      // Keep message functionality
      const div = document.createElement('div');
      div.className = 'game-toast';
      div.textContent = msg;
      div.style.cssText = `
        position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%);
        background: rgba(0,0,0,0.7); color: white; padding: 20px 30px;
        border-radius: 10px; font-size: 24px; z-index: 3000;
      `;
      document.body.appendChild(div);
      // Track the node + timer so teardown() can clear a toast still on screen.
      const entry = { el: div, timer: setTimeout(() => { div.remove(); activeMessages.delete(entry); }, duration) };
      activeMessages.add(entry);
    },
    showAudioRetryPrompt: function() {},
    clearMessages,

    // Stop the music and release the mute button (dispose-audit teardown / dev-HMR).
    // The looping <audio> element and the `#audioControlBtn` are module-level, so without
    // this they survive disposeGame — the track keeps playing and the button lingers over
    // a remount. Idempotent and inert if nothing was ever started; `initialized` is reset
    // so a later init()/setupUI() rebuilds cleanly.
    teardown: function() {
      // Clear any on-screen toasts (and their pending auto-remove timers) FIRST — showMessage
      // isn't gated on AUDIO_ENABLED, so a toast can exist even when audio is disabled.
      clearMessages();
      if (!AUDIO_ENABLED) return;
      if (audio) {
        // pause() halts playback; dropping the reference makes the (detached, paused)
        // element GC-eligible. We deliberately do NOT clear .src — assigning '' makes
        // the media element fire a spurious "empty src" error event.
        try { audio.pause(); } catch { /* detached element */ }
        audio = null;
      }
      const btn = document.getElementById('audioControlBtn');
      btn?.parentNode?.removeChild(btn);
      initialized = false;
    }
  };
})();

// The window.AudioModule bridge was removed (issue #84): every consumer now
// imports AudioModule directly — snowglider.ts (orchestrator), the boot
// script-loader, the start menu, and the audio browser test suite.
